import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Copy, Download, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Alert, AlertDescription } from "./ui/alert";

const OLIVE = { backgroundColor: "var(--hi-olive)" };
const errText = (e) => formatApiError(e?.response?.data?.detail) || e.message;

const CodeInput = ({ value, onChange, backup, testId }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} autoFocus autoComplete="one-time-code"
    inputMode={backup ? "text" : "numeric"} maxLength={backup ? 9 : 6} placeholder={backup ? "XXXX-XXXX" : "123456"}
    className="rounded-none h-12 text-center text-xl tracking-[0.4em] font-mono border-slate-300" data-testid={testId} />
);

// Step 2 of sign-in: enter the 6-digit app code (or a backup code)
export const MfaChallenge = ({ mfaToken, onSuccess, onCancel }) => {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
      if (backup) toast.warning(`Backup code used — ${data.backup_codes_left} left`, { description: "Generate new codes from My account if you're running low." });
      onSuccess(data);
    } catch (err) { setError(errText(err)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-challenge-form">
      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-5 w-5" style={{ color: "var(--hi-olive)" }} />Two-step verification</div>
      <p className="text-sm text-muted-foreground">{backup ? "Enter one of your saved backup codes. Each code works once." : "Enter the 6-digit code from your authenticator app."}</p>
      <CodeInput value={code} onChange={setCode} backup={backup} testId="mfa-code-input" />
      {error && <Alert variant="destructive" className="rounded-none" data-testid="mfa-error"><AlertDescription>{error}</AlertDescription></Alert>}
      <Button type="submit" disabled={busy || code.trim().length < 6} className="w-full h-11 rounded-none text-white" style={OLIVE} data-testid="mfa-verify-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
      </Button>
      <div className="flex justify-between text-xs">
        <button type="button" className="underline text-muted-foreground" onClick={() => { setBackup(!backup); setCode(""); setError(""); }} data-testid="mfa-toggle-backup">
          {backup ? "Use authenticator app instead" : "Lost your phone? Use a backup code"}</button>
        <button type="button" className="underline text-muted-foreground" onClick={onCancel} data-testid="mfa-cancel">Back to sign in</button>
      </div>
    </form>
  );
};

export const BackupCodes = ({ codes, onDone, doneLabel = "I've saved my codes — continue" }) => {
  const [saved, setSaved] = useState(false);
  const text = `Housing Intelligence backup codes\nEach code can be used once.\n\n${codes.join("\n")}\n`;
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = "housing-intelligence-backup-codes.txt";
    a.click(); setSaved(true);
  };
  return (
    <div className="space-y-4" data-testid="mfa-backup-codes">
      <div className="font-semibold flex items-center gap-2"><KeyRound className="h-5 w-5" />Save your backup codes</div>
      <p className="text-sm text-muted-foreground">If you lose your phone, each of these codes lets you sign in once. They won't be shown again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border p-4" data-testid="mfa-backup-code-list">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="rounded-none" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); setSaved(true); toast.success("Codes copied"); }} data-testid="mfa-copy-codes"><Copy className="h-4 w-4 mr-1" />Copy</Button>
        <Button type="button" variant="outline" className="rounded-none" onClick={download} data-testid="mfa-download-codes"><Download className="h-4 w-4 mr-1" />Download</Button>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-codes-saved-check" />I've stored these codes somewhere safe</label>
      <Button type="button" disabled={!saved} className="w-full h-11 rounded-none text-white" style={OLIVE} onClick={onDone} data-testid="mfa-codes-done-btn">{doneLabel}</Button>
    </div>
  );
};

// QR + confirm code. `startUrl`/`confirm` differ between sign-in (challenge token) and My account (session).
export const MfaSetup = ({ start, confirm, onEnrolled, onCancel, intro }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { start().then(setSetup).catch((e) => setError(errText(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { onEnrolled(await confirm(code)); } catch (err) { setError(errText(err)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-setup-form">
      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-5 w-5" style={{ color: "var(--hi-olive)" }} />Set up two-step verification</div>
      {intro && <p className="text-sm text-muted-foreground">{intro}</p>}
      <ol className="text-sm space-y-1 list-decimal ml-4 text-muted-foreground">
        <li>Open Google Authenticator, Microsoft Authenticator or Authy.</li>
        <li>Scan this QR code (or enter the key by hand).</li>
        <li>Type the 6-digit code it shows.</li>
      </ol>
      {setup ? (
        <div className="flex items-center gap-4">
          <img src={setup.qr} alt="MFA QR code" className="h-40 w-40 border" data-testid="mfa-qr" />
          <div className="text-xs space-y-1"><div className="text-muted-foreground">Setup key</div>
            <div className="font-mono break-all bg-slate-50 border p-2" data-testid="mfa-secret">{setup.secret.match(/.{1,4}/g).join(" ")}</div></div>
        </div>
      ) : !error && <Loader2 className="h-5 w-5 animate-spin" />}
      <CodeInput value={code} onChange={setCode} testId="mfa-setup-code-input" />
      {error && <Alert variant="destructive" className="rounded-none" data-testid="mfa-setup-error"><AlertDescription>{error}</AlertDescription></Alert>}
      <Button type="submit" disabled={busy || !setup || code.trim().length !== 6} className="w-full h-11 rounded-none text-white" style={OLIVE} data-testid="mfa-setup-confirm-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Turn on two-step verification"}
      </Button>
      {onCancel && <button type="button" className="text-xs underline text-muted-foreground" onClick={onCancel} data-testid="mfa-setup-cancel">Cancel</button>}
    </form>
  );
};
